import { useMemo, useState } from 'react';
import type {
  AdminAccountAssistant,
  AdminAssistantSettings,
  AdminAssistantStats,
  AdminProviderModel,
  AdminProviderModels,
  AssistantEffort,
  AssistantEvaluation,
  AssistantSettings,
} from '@boardflight/shared';
import { AssistantEffort as AssistantEfforts } from '@boardflight/shared';
import { ApiError, api, post, put } from '../api.js';
import { Async, Panel, Time, useAsync } from '../ui.js';
import { HEIGHT, PAD, niceTicks, useWidth } from './Active.js';

/**
 * The creators' assistant (`docs/assistant-plan.md` §11).
 *
 * Settings first, because they are what an operator comes here to change: the model, how long an
 * answer and how many lookups, and the two limits on money. Then how it is used — over a range, by
 * day, by how questions ended, by model, by lookup and by the documentation read — and the month's
 * highest spenders. Nothing on this page holds a question or an answer; nothing it reads does
 * either (§8).
 *
 * Which numbers count whom is said on the page, once, above them: spend counts every account,
 * BoardFlight's own included, because it is money; everything else leaves the excluded accounts
 * out, because it is a measure of use.
 */

/* --------------------------------------------------------------------------------- format */

const dollars = (micro: number | null | undefined): string => {
  if (micro === null || micro === undefined) return '—';
  const value = micro / 1_000_000;
  return `$${value.toFixed(value !== 0 && Math.abs(value) < 0.1 ? 4 : 2)}`;
};

const seconds = (ms: number | null | undefined): string =>
  ms === null || ms === undefined ? '—' : `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;

const share = (part: number, whole: number): string => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

const utcDay = (at: number): string => new Date(at).toISOString().slice(0, 10);

/* ------------------------------------------------------------------------------- the page */

export function Assistant() {
  // The numbers are read every visit and the settings rarely changed (owner, 2026-10-28): the
  // numbers first, the form one press away.
  const [settings, setSettings] = useState(false);
  // Whether it is answering at all is the one setting worth seeing without opening the form.
  const current = useAsync(() => api<AdminAssistantSettings>('/v1/admin/assistant/settings'), [settings]);
  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        {current.data ? (
          <span className={current.data.settings.enabled ? 'pill on' : 'pill off'}>
            {current.data.settings.enabled ? 'Answering' : 'Switched off'}
          </span>
        ) : null}
        {current.data ? <span className="muted small mono">{current.data.settings.model}</span> : null}
        <button className={settings ? 'primary' : undefined} onClick={() => setSettings(!settings)}>
          {settings ? 'Hide settings' : 'Settings and model'}
        </button>
      </div>
      {settings ? <Settings /> : null}
      <Statistics />
    </>
  );
}

/* --------------------------------------------------------------------------------- settings */

/** The form's own copy: money in dollars and counts as typed, until Save turns them into the row. */
interface Draft {
  enabled: boolean;
  model: string;
  effort: AssistantEffort | '';
  maxOutputTokens: string;
  maxToolCalls: string;
  conversationTokenLimit: string;
  accountDay: string;
  month: string;
}

function draftOf(settings: AssistantSettings): Draft {
  return {
    enabled: settings.enabled,
    model: settings.model,
    effort: settings.effort ?? '',
    maxOutputTokens: String(settings.maxOutputTokens),
    maxToolCalls: String(settings.maxToolCalls),
    conversationTokenLimit: String(settings.conversationTokenLimit),
    accountDay: (settings.accountDayMicro / 1_000_000).toFixed(2),
    month: (settings.monthMicro / 1_000_000).toFixed(2),
  };
}

function settingsOf(draft: Draft): AssistantSettings {
  return {
    enabled: draft.enabled,
    model: draft.model.trim(),
    effort: draft.effort === '' ? null : draft.effort,
    maxOutputTokens: Number(draft.maxOutputTokens),
    maxToolCalls: Number(draft.maxToolCalls),
    conversationTokenLimit: Number(draft.conversationTokenLimit),
    accountDayMicro: Math.round(Number(draft.accountDay) * 1_000_000),
    monthMicro: Math.round(Number(draft.month) * 1_000_000),
  };
}

function Settings() {
  const state = useAsync(() => api<AdminAssistantSettings>('/v1/admin/assistant/settings'), []);
  return (
    <Async state={state}>
      {(data) => <SettingsForm key={data.updatedAt ?? 0} initial={data} onSaved={state.reload} />}
    </Async>
  );
}

function SettingsForm({ initial, onSaved }: { initial: AdminAssistantSettings; onSaved: () => void }) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(initial.settings));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const change = (next: Partial<Draft>) => setDraft((current) => ({ ...current, ...next }));
  const changed = JSON.stringify(settingsOf(draft)) !== JSON.stringify(initial.settings);
  const evaluation = initial.evaluations.find((one) => one.model === draft.model.trim());

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await put<AdminAssistantSettings>('/v1/admin/assistant/settings', settingsOf(draft));
      onSaved();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Panel
        title="Assistant"
        actions={
          <span className={initial.settings.enabled ? 'pill ok' : 'pill'}>
            {initial.settings.enabled ? 'Answering' : 'Switched off'}
          </span>
        }
      >
        <form className="form-grid" onSubmit={(event) => void save(event)}>
          <label className="row wide">
            <input type="checkbox" checked={draft.enabled} onChange={(event) => change({ enabled: event.target.checked })} />
            Answering questions
            <span className="note">
              Off, the panel says the assistant is not answering right now and nothing is sent anywhere. It still shows
              only to accounts with the assistant granted (the Features tab).
            </span>
          </label>
          <label>
            <span className="muted small">Model (Cheaper Inference id)</span>
            <input type="text" value={draft.model} onChange={(event) => change({ model: event.target.value })} spellCheck={false} />
            <span className="note">
              {evaluation ? <EvaluationLine evaluation={evaluation} /> : 'Not through the eval: run scripts/assistant-eval first.'}
            </span>
          </label>
          <label>
            <span className="muted small">Reasoning effort</span>
            <select value={draft.effort} onChange={(event) => change({ effort: event.target.value as AssistantEffort | '' })}>
              <option value="">The model’s default</option>
              {AssistantEfforts.options.map((effort) => (
                <option key={effort} value={effort}>
                  {effort}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="muted small">Longest answer (tokens)</span>
            <input type="number" min={256} max={32000} value={draft.maxOutputTokens} onChange={(event) => change({ maxOutputTokens: event.target.value })} />
          </label>
          <label>
            <span className="muted small">Lookups per question</span>
            <input type="number" min={0} max={20} value={draft.maxToolCalls} onChange={(event) => change({ maxToolCalls: event.target.value })} />
            <span className="note">After this many, it must answer with what it found.</span>
          </label>
          <label>
            <span className="muted small">Conversation limit (tokens)</span>
            <input
              type="number"
              min={8000}
              value={draft.conversationTokenLimit}
              onChange={(event) => change({ conversationTokenLimit: event.target.value })}
            />
            <span className="note">Past it, the oldest lookups and then turns are dropped.</span>
          </label>
          <label>
            <span className="muted small">Per account, per UTC day ($)</span>
            <input type="number" min={0} step="0.01" value={draft.accountDay} onChange={(event) => change({ accountDay: event.target.value })} />
          </label>
          <label>
            <span className="muted small">Everybody, per calendar month ($)</span>
            <input type="number" min={0} step="0.01" value={draft.month} onChange={(event) => change({ month: event.target.value })} />
            <span className="note">Spend is what Cheaper Inference billed for each request, as it said.</span>
          </label>
          <div className="row wide">
            <button className="primary" type="submit" disabled={busy || !changed}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            {changed ? (
              <button type="button" onClick={() => setDraft(draftOf(initial.settings))}>
                Undo changes
              </button>
            ) : null}
            <span className="muted small">
              {initial.updatedAt === null ? 'As first set up; nobody has changed it.' : <>Last changed <Time at={initial.updatedAt} />.</>}
            </span>
          </div>
          {error ? <p className="error wide">{error}</p> : null}
        </form>
        <p className="note">
          The inference key is a Worker secret on the API and is not set here. The prompt is code: the rules in it are what
          keep plans and names out of answers.
        </p>
      </Panel>
      <ModelPicker evaluations={initial.evaluations} onPick={(model) => change({ model })} current={draft.model.trim()} />
    </>
  );
}

function EvaluationLine({ evaluation }: { evaluation: AssistantEvaluation }) {
  return (
    <>
      Eval {evaluation.at}: {evaluation.passed} of {evaluation.graded}
      {evaluation.costPerQuestionMicro > 0 ? `, ${dollars(evaluation.costPerQuestionMicro)} a question` : ''}
      {evaluation.medianMs > 0 ? `, ${seconds(evaluation.medianMs)} median` : ''}. {evaluation.verdict === 'chosen' ? 'The eval’s choice.' : evaluation.verdict === 'alternative' ? 'The alternative.' : 'Not recommended.'}{' '}
      {evaluation.note}
    </>
  );
}

/** How many rows of the catalogue are drawn at once; the filter finds the rest. */
const SHOWN = 60;

/**
 * Cheaper Inference's catalogue, for picking a model by its own id: the same request the AI models
 * tab makes (`POST /ai-provider-models`), with the key pasted for that one request and kept by
 * nothing — the field empties as it is sent.
 */
function ModelPicker({ evaluations, onPick, current }: {
  evaluations: readonly AssistantEvaluation[];
  onPick: (model: string) => void;
  current: string;
}) {
  const [key, setKey] = useState('');
  const [loaded, setLoaded] = useState<AdminProviderModels | null>(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const body = { provider: 'cheaperinference', key };
    setKey('');
    try {
      setLoaded(await post<AdminProviderModels>('/v1/admin/ai-provider-models', body));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const matching = useMemo(() => {
    if (!loaded) return [];
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const evaluated = new Set(evaluations.map((one) => one.model));
    return loaded.items
      .filter((item) => item.tools !== false && words.every((word) => item.id.toLowerCase().includes(word)))
      // The models the eval has measured first, then by price: what an operator chooses between.
      .sort((a, b) => Number(evaluated.has(b.id)) - Number(evaluated.has(a.id)) || (a.price?.output ?? 1e9) - (b.price?.output ?? 1e9));
  }, [loaded, filter, evaluations]);

  return (
    <Panel title="Pick a model from Cheaper Inference">
      <form className="row" onSubmit={(event) => void load(event)}>
        <input
          type="password"
          value={key}
          onChange={(event) => setKey(event.target.value)}
          placeholder="Cheaper Inference key"
          aria-label="Cheaper Inference key"
          autoComplete="off"
          spellCheck={false}
          required
        />
        <button type="submit" disabled={busy}>
          {busy ? 'Loading…' : 'Load models'}
        </button>
      </form>
      <p className="note">
        The key goes to the admin Worker for this one request and is kept nowhere. Only models that take tools are listed;
        the ones the eval measured come first.
      </p>
      {error ? <p className="error">{error}</p> : null}
      {loaded ? (
        <>
          <div className="row">
            <input type="text" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filter by id" aria-label="Filter models" />
            <span className="muted small">
              {matching.length} take tools{matching.length > SHOWN ? `, first ${SHOWN} shown` : ''}
            </span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Id</th>
                  <th className="num">In / out per M</th>
                  <th className="num">Cache read</th>
                  <th>Caching</th>
                  <th>ZDR route</th>
                  <th className="num">Context</th>
                  <th>Eval</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {matching.slice(0, SHOWN).map((item) => (
                  <CatalogueRow
                    key={item.id}
                    item={item}
                    evaluation={evaluations.find((one) => one.model === item.id)}
                    chosen={item.id === current}
                    onPick={() => onPick(item.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Panel>
  );
}

function CatalogueRow({ item, evaluation, chosen, onPick }: {
  item: AdminProviderModel;
  evaluation: AssistantEvaluation | undefined;
  chosen: boolean;
  onPick: () => void;
}) {
  return (
    <tr>
      <td className="mono nowrap">{item.id}</td>
      <td className="num nowrap">{item.price ? `$${item.price.input} / $${item.price.output}` : '—'}</td>
      <td className="num">{item.price?.cacheRead !== null && item.price?.cacheRead !== undefined ? `$${item.price.cacheRead}` : '—'}</td>
      <td>{item.caching ?? 'none said'}</td>
      <td>{item.zdrRoute ?? '—'}</td>
      <td className="num">{item.contextLength ? `${Math.round(item.contextLength / 1000)}k` : '—'}</td>
      <td className="small">{evaluation ? `${evaluation.passed}/${evaluation.graded}, ${evaluation.verdict}` : 'not run'}</td>
      <td>
        <button type="button" disabled={chosen} onClick={onPick}>
          {chosen ? 'Chosen' : 'Use this'}
        </button>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------------------- statistics */

/** The Analytics tab's ranges, as UTC days back from today, so the two tabs read the same days. */
const RANGES = [
  { value: 'today', label: 'Today', from: 0, to: 0 },
  { value: 'yesterday', label: 'Yesterday', from: 1, to: 1 },
  { value: '7', label: 'Last 7 days', from: 6, to: 0 },
  { value: '30', label: 'Last 30 days', from: 29, to: 0 },
  { value: '90', label: 'Last 90 days', from: 89, to: 0 },
] as const;
type RangeValue = (typeof RANGES)[number]['value'];

const OUTCOME_LABELS: Record<string, string> = {
  answered: 'Answered',
  day_limit: 'Stopped at the person’s day limit',
  month_limit: 'Stopped at the month’s limit',
  stopped: 'Stopped by the person',
  lookups_spent: 'Ran out of lookups',
  provider_error: 'The provider failed',
  error: 'Our own error',
};

function Statistics() {
  const [range, setRange] = useState<RangeValue>('30');
  const picked = RANGES.find((one) => one.value === range) ?? RANGES[3];
  const from = utcDay(Date.now() - picked.from * 86_400_000);
  const to = utcDay(Date.now() - picked.to * 86_400_000);
  const state = useAsync(() => api<AdminAssistantStats>(`/v1/admin/assistant/stats?from=${from}&to=${to}`), [from, to]);

  return (
    <Panel
      title="How it is used"
      actions={
        <select value={range} onChange={(event) => setRange(event.target.value as RangeValue)} aria-label="Range">
          {RANGES.map((one) => (
            <option key={one.value} value={one.value}>
              {one.label}
            </option>
          ))}
        </select>
      }
    >
      <Async state={state}>{(data) => <StatisticsBody data={data} />}</Async>
    </Panel>
  );
}

function StatisticsBody({ data }: { data: AdminAssistantStats }) {
  const { spend, use, limits } = data;
  const monthShare = limits.monthMicro > 0 ? spend.monthMicro / limits.monthMicro : null;

  return (
    <>
      <p className="note">
        Spend counts every account, BoardFlight’s own included: it is money. Everything else leaves out the{' '}
        {data.excludedAccounts} excluded {data.excludedAccounts === 1 ? 'account' : 'accounts'} (Accounts tab). UTC days, {data.from}
        {data.from === data.to ? '' : ` to ${data.to}`}. No question or answer is kept anywhere this page could show it.
      </p>

      <div className="stats">
        <Stat label="Questions" value={use.questions.toLocaleString()} />
        <Stat label="People" value={use.people.toLocaleString()} />
        <Stat label="Conversations" value={use.conversations.toLocaleString()} />
        <Stat
          label="Spend in range"
          value={dollars(spend.rangeMicro)}
          sub={spend.excludedMicro > 0 ? `${dollars(spend.excludedMicro)} of it excluded accounts` : undefined}
        />
        <Stat label="Cost per question" value={dollars(spend.costPerQuestionMicro)} />
        <Stat label="Today" value={dollars(spend.todayMicro)} />
        <Stat
          label="This month"
          value={`${dollars(spend.monthMicro)} of ${dollars(limits.monthMicro)}`}
          sub={spend.monthPaceMicro !== null ? `on pace for ${dollars(spend.monthPaceMicro)}` : undefined}
          tone={monthShare !== null && monthShare >= 0.8 ? 'warn' : undefined}
        />
        <Stat label="First word, median / p90" value={`${seconds(use.firstTokenMs.p50)} / ${seconds(use.firstTokenMs.p90)}`} />
        <Stat label="Whole answer, median / p90" value={`${seconds(use.totalMs.p50)} / ${seconds(use.totalMs.p90)}`} />
        <Stat label="Thumbs up" value={share(use.up, use.rated)} sub={`${use.rated} of ${use.questions} rated`} />
        <Stat label="Lookups per question" value={use.questions > 0 ? (use.lookups / use.questions).toFixed(1) : '—'} />
      </div>

      <Bars title="Questions" days={data.days.map((day) => day.day)} values={data.days.map((day) => day.questions)} format={String} />
      <Bars title="People" days={data.days.map((day) => day.day)} values={data.days.map((day) => day.people)} format={String} />
      <Bars title="Spend (everybody)" days={data.days.map((day) => day.day)} values={data.days.map((day) => day.spendMicro)} format={dollars} />
      <Bars title="First word, mean" days={data.days.map((day) => day.day)} values={data.days.map((day) => day.firstTokenAvgMs)} format={seconds} />

      <h3>How questions ended</h3>
      {data.outcomes.length === 0 ? (
        <p className="muted">No questions in this range.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ended</th>
                <th className="num">Questions</th>
                <th className="num">Share</th>
              </tr>
            </thead>
            <tbody>
              {data.outcomes.map((row) => (
                <tr key={`${row.outcome}-${row.providerStatus ?? ''}`}>
                  <td className={row.outcome === 'answered' ? undefined : 'warn'}>
                    {OUTCOME_LABELS[row.outcome] ?? row.outcome}
                    {row.providerStatus ? ` (${row.providerStatus})` : ''}
                  </td>
                  <td className="num">{row.questions}</td>
                  <td className="num">{share(row.questions, use.questions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Per model</h3>
      {data.models.length === 0 ? (
        <p className="muted">No questions in this range.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th className="num">Questions</th>
                <th className="num">People</th>
                <th className="num">Not answered</th>
                <th className="num">First word</th>
                <th className="num">Up / down</th>
                <th className="num">Spend</th>
                <th className="num">Per question</th>
              </tr>
            </thead>
            <tbody>
              {data.models.map((row) => (
                <tr key={row.model}>
                  <td className="mono nowrap">
                    {row.model}
                    {row.model === limits.model ? <span className="muted small"> (current)</span> : null}
                  </td>
                  <td className="num">{row.questions}</td>
                  <td className="num">{row.people}</td>
                  <td className="num">{row.notAnswered}</td>
                  <td className="num">{seconds(row.firstTokenAvgMs)}</td>
                  <td className="num">
                    {row.up} / {row.down}
                  </td>
                  <td className="num">{dollars(row.spendMicro)}</td>
                  <td className="num">{dollars(row.costPerQuestionMicro)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="row" style={{ alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <h3>Lookups</h3>
          {data.tools.length === 0 ? (
            <p className="muted">None.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Tool</th>
                  <th className="num">Calls</th>
                  <th className="num">Failed</th>
                </tr>
              </thead>
              <tbody>
                {data.tools.map((row) => (
                  <tr key={row.tool}>
                    <td className="mono">{row.tool}</td>
                    <td className="num">{row.calls}</td>
                    <td className={row.failed > 0 ? 'num warn' : 'num'}>{row.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <h3>Who asked how often</h3>
          <table>
            <tbody>
              <tr>
                <td>Once</td>
                <td className="num">{data.perPerson.once}</td>
              </tr>
              <tr>
                <td>2 to 9 times</td>
                <td className="num">{data.perPerson.few}</td>
              </tr>
              <tr>
                <td>10 or more</td>
                <td className="num">{data.perPerson.many}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h3>Documentation read most</h3>
      <p className="note">The sections the assistant opened to answer. A guide that is read often and rated down is the one to improve.</p>
      {data.docs.length === 0 ? (
        <p className="muted">None in this range.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Section</th>
                <th className="num">Reads</th>
                <th className="num" title="Reads in answers the asker rated down">Rated down</th>
              </tr>
            </thead>
            <tbody>
              {data.docs.map((row) => (
                <tr key={row.docId}>
                  <td className="mono">{row.docId}</td>
                  <td className="num">{row.reads}</td>
                  <td className={row.ratedDown ? 'num error' : 'num muted'}>{row.ratedDown || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Highest spenders this month</h3>
      <p className="note">Money, so every account is here; an excluded one is marked rather than left out.</p>
      {data.spenders.length === 0 ? (
        <p className="muted">Nobody yet this month.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th className="num">Questions</th>
                <th className="num">Spend</th>
                <th className="num">Per question</th>
              </tr>
            </thead>
            <tbody>
              {data.spenders.map((row) => (
                <tr key={row.userId ?? 'deleted'}>
                  <td>
                    {row.userId ? <a href={`#accounts/${row.userId}/assistant`}>@{row.username}</a> : <span className="muted">a deleted account</span>}
                    {row.excluded ? (
                      <>
                        {' '}
                        <span className="pill">excluded</span>
                      </>
                    ) : null}
                  </td>
                  <td className="num">{row.questions}</td>
                  <td className="num">{dollars(row.spendMicro)}</td>
                  <td className="num">{row.questions > 0 ? dollars(Math.round(row.spendMicro / row.questions)) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'warn' }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className={tone ? `stat-value ${tone}` : 'stat-value'}>{value}</span>
      {sub ? <span className="muted small">{sub}</span> : null}
    </div>
  );
}

/**
 * One measure, a bar a day. A bar's own title is its tooltip; a day with nothing to measure (a mean
 * of no answers) has no bar, which is not the same as a bar of zero.
 */
function Bars({ title, days, values, format }: {
  title: string;
  days: string[];
  values: (number | null)[];
  format: (value: number) => string;
}) {
  const [ref, width] = useWidth();
  const ticks = niceTicks(Math.max(1, ...values.map((value) => value ?? 0)));
  const top = ticks[ticks.length - 1] ?? 1;
  const plotWidth = Math.max(0, width - PAD.left - PAD.right);
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const slot = days.length > 0 ? plotWidth / days.length : 0;
  const y = (value: number) => PAD.top + plotHeight - (value / top) * plotHeight;
  const every = Math.max(1, Math.ceil(days.length / Math.max(1, Math.floor(plotWidth / 90))));
  const last = days.length - 1;

  return (
    <div className="chart">
      <div className="chart-head">
        <span className="chart-title">{title}</span>
      </div>
      <div className="chart-plot" ref={ref}>
        {width > 0 ? (
          <svg width={width} height={HEIGHT} role="img" aria-label={`${title}, by day`}>
            {ticks.map((tick) => (
              <g key={tick}>
                <line className="chart-grid" x1={PAD.left} x2={PAD.left + plotWidth} y1={y(tick)} y2={y(tick)} />
                <text className="chart-axis" x={PAD.left - 6} y={y(tick)} dy="0.32em" textAnchor="end">
                  {format(tick)}
                </text>
              </g>
            ))}
            {values.map((value, index) =>
              value === null ? null : (
                <rect
                  key={days[index]}
                  x={PAD.left + index * slot + slot * 0.15}
                  width={Math.max(1, slot * 0.7)}
                  y={y(value)}
                  height={Math.max(0, PAD.top + plotHeight - y(value))}
                  fill="var(--series-1)"
                >
                  <title>
                    {days[index]}: {format(value)}
                  </title>
                </rect>
              ),
            )}
            {days.map((day, index) =>
              index % every === 0 || index === last ? (
                index !== last && last - index < every / 2 ? null : (
                  <text
                    key={day}
                    className="chart-axis"
                    x={PAD.left + index * slot + slot / 2}
                    y={HEIGHT - 6}
                    textAnchor={index === 0 && days.length > 1 ? 'start' : index === last && days.length > 1 ? 'end' : 'middle'}
                  >
                    {day === utcDay(Date.now()) ? 'today' : day.slice(5)}
                  </text>
                )
              ) : null,
            )}
          </svg>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------ one account */

/**
 * The account page's Assistant tab (§11.3): that account's own record — questions and spend by day,
 * its day limit and how much of today it has used, how many conversations it holds. Counts only.
 */
export function AccountAssistant({ userId }: { userId: string }) {
  const state = useAsync(() => api<AdminAccountAssistant>(`/v1/admin/accounts/${userId}/assistant`), [userId]);
  return (
    <Panel title="Assistant">
      <Async state={state}>
        {(data) => {
          const asked = data.days.filter((day) => day.questions > 0);
          return (
            <>
              <div className="stats">
                <Stat label="Today" value={`${dollars(data.todayMicro)} of ${dollars(data.accountDayMicro)}`} />
                <Stat label="Questions, 30 days" value={String(asked.reduce((n, day) => n + day.questions, 0))} />
                <Stat label="Spend, 30 days" value={dollars(asked.reduce((n, day) => n + day.spendMicro, 0))} />
                <Stat label="Conversations kept" value={String(data.conversations)} />
              </div>
              <p className="note">
                Its own record, so nothing is left out for being on the exclusion list. What it asked is not kept anywhere
                this page could show it.
              </p>
              {asked.length === 0 ? (
                <p className="muted">No questions in the last 30 days.</p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Day (UTC)</th>
                        <th className="num">Questions</th>
                        <th className="num">Spend</th>
                        <th className="num">Up / down</th>
                      </tr>
                    </thead>
                    <tbody>
                      {asked.map((day) => (
                        <tr key={day.day}>
                          <td>{day.day}</td>
                          <td className="num">{day.questions}</td>
                          <td className="num">{dollars(day.spendMicro)}</td>
                          <td className="num">
                            {day.up} / {day.down}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        }}
      </Async>
    </Panel>
  );
}
